import React, { useMemo, useState } from "react";
import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import type { FrontendErrorGroup } from "@/features/observability/types/frontend-errors.types";

type Props = {
    groups: FrontendErrorGroup[];
    asOf: number;
    lowerBound: boolean;
    truncated: boolean;
    selectedKey: string | null;
    onOpen: (group: FrontendErrorGroup) => void;
};

type SortKey = "count" | "devices" | "first" | "last";

const PAGE_SIZE = 20;
const MAX_RELEASES = 5;

const SORT_VALUE: Record<SortKey, (group: FrontendErrorGroup) => number | null> = {
    count: (group) => group.count,
    devices: (group) => group.affectedDevicesApprox,
    first: (group) => group.firstSeenAt,
    last: (group) => group.lastSeenAt,
};

// Unknown values sort after every known one in both directions: a missing date is never "the newest".
const compare = (key: SortKey, descending: boolean) => (a: FrontendErrorGroup, b: FrontendErrorGroup): number => {
    const left = SORT_VALUE[key](a);
    const right = SORT_VALUE[key](b);
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return descending ? right - left : left - right;
};

const matches = (group: FrontendErrorGroup, needle: string): boolean =>
    [group.errorCode, group.messageTemplate, group.route, group.errorClass].some(
        (value) => value !== null && value.toLowerCase().includes(needle),
    );

export function FrontendErrorsGroupsTable({ groups, asOf, lowerBound, truncated, selectedKey, onOpen }: Props) {
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState<{ key: SortKey; descending: boolean }>({ key: "count", descending: true });
    const [page, setPage] = useState(0);

    const rows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        const filtered = needle === "" ? groups : groups.filter((group) => matches(group, needle));
        return filtered.slice().sort(compare(sort.key, sort.descending));
    }, [groups, search, sort]);

    const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const current = Math.min(page, pages - 1);
    const visible = rows.slice(current * PAGE_SIZE, current * PAGE_SIZE + PAGE_SIZE);

    const sortBy = (key: SortKey): void => {
        setSort((previous) => ({ key, descending: previous.key === key ? !previous.descending : true }));
        setPage(0);
    };

    const indicator = (key: SortKey): string => {
        if (sort.key !== key) return "";
        return sort.descending ? " ↓" : " ↑";
    };

    const ariaSort = (key: SortKey): "ascending" | "descending" | "none" => {
        if (sort.key !== key) return "none";
        return sort.descending ? "descending" : "ascending";
    };

    return (
        <div className="fe-card fe-groups">
            <div className="fe-card__hd fe-groups__hd">
                <div className="fe-groups__title">
                    <div className="fe-card__title">Grupos</div>
                    <div className="fe-muted">{`${frontendErrorsFormat.count(rows.length)} fingerprints · clic para ver eventos`}</div>
                </div>
                <label className="fe-search">
                    <span className="material-symbols-outlined">search</span>
                    <input
                        aria-label="Buscar código, ruta o clase"
                        placeholder="Buscar código, ruta o clase"
                        value={search}
                        onChange={(event) => {
                            setSearch(event.target.value);
                            setPage(0);
                        }}
                    />
                </label>
            </div>
            {truncated ? (
                <div className="fe-notice fe-notice--warn fe-groups__notice">
                    <span className="material-symbols-outlined">warning</span>
                    Hay más grupos de los que se muestran: la fuente ha recortado la lista.
                </div>
            ) : null}
            <div className="tbl-wrap fe-groups__wrap">
                <table className="tbl fe-groups__tbl">
                    <thead>
                        <tr>
                            <th>Categoría</th>
                            <th>Código de error</th>
                            <th>Clase</th>
                            <th>Ruta</th>
                            <th>HTTP</th>
                            <th className="td-num" aria-sort={ariaSort("count")}>
                                <button type="button" className="fe-sort" onClick={() => sortBy("count")}>{`Ocurrencias${indicator("count")}`}</button>
                            </th>
                            <th className="td-num" aria-sort={ariaSort("devices")}>
                                <button type="button" className="fe-sort" onClick={() => sortBy("devices")}>{`Dispositivos${indicator("devices")}`}</button>
                            </th>
                            <th aria-sort={ariaSort("first")}>
                                <button type="button" className="fe-sort" onClick={() => sortBy("first")}>{`Primera vez${indicator("first")}`}</button>
                            </th>
                            <th aria-sort={ariaSort("last")}>
                                <button type="button" className="fe-sort" onClick={() => sortBy("last")}>{`Última vez${indicator("last")}`}</button>
                            </th>
                            <th>Releases</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((group) => {
                            const style = frontendErrorsFormat.categoryStyle(group.category);
                            const key = frontendErrorsFormat.groupKey(group);
                            const openable = group.fingerprint !== null;
                            const releases = group.releases;
                            return (
                                <tr
                                    key={key}
                                    data-testid="fe-group-row"
                                    className={`fe-groups__row${openable ? " fe-groups__row--open" : ""}${openable && key === selectedKey ? " fe-groups__row--selected" : ""}`}
                                    tabIndex={openable ? 0 : -1}
                                    onClick={() => {
                                        if (openable) onOpen(group);
                                    }}
                                    onKeyDown={(event) => {
                                        if (openable && event.key === "Enter") onOpen(group);
                                    }}
                                >
                                    <td>
                                        <span className="fe-chip">
                                            <span className="fe-swatch" style={{ background: style.color }} />
                                            {style.label}
                                        </span>
                                    </td>
                                    <td className="td-strong fe-ellipsis">
                                        {group.errorCode === null ? "—" : group.errorCode}
                                        {group.messageTemplate === null ? null : (
                                            <div className="fe-muted fe-ellipsis" title={group.messageTemplate}>{group.messageTemplate}</div>
                                        )}
                                    </td>
                                    <td className="td-muted">{group.errorClass === null ? "—" : group.errorClass}</td>
                                    <td className="td-muted fe-ellipsis">{group.route === null ? "—" : group.route}</td>
                                    <td className="td-muted">{group.httpStatus === null ? "—" : String(group.httpStatus)}</td>
                                    <td className="td-num td-strong">{`${frontendErrorsFormat.count(group.count)}${lowerBound ? " mín." : ""}`}</td>
                                    <td className="td-num">{frontendErrorsFormat.count(group.affectedDevicesApprox)}</td>
                                    <td className="td-muted">{frontendErrorsFormat.nullableDateTime(group.firstSeenAt)}</td>
                                    <td className="td-muted">
                                        {group.lastSeenAt === null ? "—" : frontendErrorsFormat.sinceAsOf(group.lastSeenAt, asOf)}
                                    </td>
                                    <td>
                                        {releases === null || releases.length === 0 ? (
                                            <span className="td-muted">—</span>
                                        ) : (
                                            <span className="fe-releases" title="hasta 5 releases">
                                                {releases.slice(0, MAX_RELEASES).map((release) => (
                                                    <span key={release} className="fe-code-chip" title={release}>{frontendErrorsFormat.short(release)}</span>
                                                ))}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {rows.length === 0 ? <div className="fe-groups__empty">Ningún grupo coincide con los filtros.</div> : null}
            </div>
            <div className="fe-pager">
                <span>{rows.length === 0 ? "" : `${current * PAGE_SIZE + 1}–${Math.min(rows.length, current * PAGE_SIZE + PAGE_SIZE)} de ${rows.length}`}</span>
                <div className="fe-pager__btns">
                    <button type="button" className="btn btn--icon btn--sm" aria-label="Página anterior" disabled={current === 0} onClick={() => setPage(current - 1)}>
                        <span className="material-symbols-outlined">chevron_left</span>
                    </button>
                    <button type="button" className="btn btn--icon btn--sm" aria-label="Página siguiente" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>
                        <span className="material-symbols-outlined">chevron_right</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
